<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { GraphqlDatatableColumn } from './GqlDatatable.vue';
import { useAuthStore } from '~/stores/auth';
import { isNilOrEmpty } from '~/utils';
import referenciaGcpjAndamentoSelect from '~/graphql/referenciaGcpjAndamentoSelect.graphql';
import agenciaEmailSelect from '~/graphql/agenciaEmailSelect.graphql';
import pastaGqlQuery from '~/graphql/pasta.graphql';
import { useDrawerStore } from '~/stores/drawer';

const props = defineProps({
  compromisso: {
    default: null,
    type: Object,
  },
  pastaCodigos: {
    default: () => null,
    type: Array,
  },
});

const emit = defineEmits(['close', 'loaded', 'success', 'error']);

const submitStatus = ref<any>(null);
const mostrarHistoricoSolicitacoes = ref<any>('h');

const colunas = [
  {
    key: 'compromisso',
    title: 'COMPROMISSO',
    width: 120,
  },
  {
    key: 'responsavel',
    title: 'RESPONSÁVEL',
    width: 120,
  },
  {
    dataIndex: 'dataInicioText',
    key: 'dataInicio',
    sortBy: ['dataInicio'],
    sorter: true,
    title: 'SOLICITADO EM',
    width: 120,
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'dataVencimentoText',
    key: 'dataVencimento',
    sortBy: ['dataVencimento'],
    sorter: true,
    title: 'VENCIMENTO EM',
    width: 120,
  },
  {
    dataIndex: 'dataConclusaoText',
    key: 'dataConclusao',
    sortBy: ['dataConclusao'],
    sorter: true,
    title: 'CONCLUÍDO EM',
    width: 120,
  },
] as GraphqlDatatableColumn[];

const agenciaEmailsRef = ref<any>(null);

const authStore = useAuthStore();
const drawerStore = useDrawerStore();

function abrirNovoAgenciaEmailDrawer(agenciaCodigo: string)
{
  drawerStore.push2({
    componentName: 'NovoAgenciaEmailDrawer',
    params: {
      agenciaCodigo,
    },
    onClose: async () =>
    {
      await agenciaEmailsRef.value.reloadAsync();
    },
  });
}

onMounted(() =>
{
  emit('loaded');
});
</script>

<template>
  <div>
    <div v-if="submitStatus !== 'success'">
      <a-collapse
        v-if="props.pastaCodigos?.length === 1"
        v-model:activeKey="mostrarHistoricoSolicitacoes"
      >
        <a-collapse-panel
          key="h"
          header="HISTÓRICO DE SOLICITAÇÕES"
        >
          <CompromissoGqlDatatable
            :columns="colunas"
            :show-dates-filter="false"
            :show-reload-button="false"
            :show-status-search-bar="false"
            :show-filter="false"
            :variables="{
              order: [{ dataInicio: 'DESC' }],
              where: {
                tipo: {
                  eq: 161,
                },
                fluxo: {
                  pastaCodigo: {
                    in: props.pastaCodigos,
                  },
                },
              },
            }"
          />
        </a-collapse-panel>
      </a-collapse>
    </div>

    <div style="margin-top: 16px" />

    <GqlView
      :query="pastaGqlQuery"
      :variables="{ codigo: props.pastaCodigos[0] }"
    >
      <template #success="{ data }: {data: any}">
        <CompromissoJobCommandForm
          command="SolicitarSubsidioCommand"
          :show-upload-files="true"
          :submit-button-disabled="!isNilOrEmpty(compromisso)
            && compromisso.responsavel.codigo !== authStore.codigoUsuario"
          :initial-model="{
            pastaCodigos: props.pastaCodigos,
            compromissoCodigos: props.compromisso ? [props.compromisso.codigo] : null,
          }"
          @close="() => emit('close')"
        >
          <template #form="{ model }">
            <a-form-item
              label="Precisa de subsídio?"
              name="precisaSubsidio"
              :rules="{ required: true, message: 'Esse item é obrigatório' }"
            >
              <a-radio-group
                v-model:value="model.precisaSubsidio"
              >
                <a-radio-button :value="true">
                  Sim
                </a-radio-button>
                <a-radio-button :value="false">
                  Não
                </a-radio-button>
              </a-radio-group>
            </a-form-item>

            <a-form-item
              v-if="model.precisaSubsidio"
              label="Referência GCPJ"
              name="referenciaGcpj"
              :rules="{ required: true, message: 'Referência GCPJ é obrigatória' }"
            >
              <GqlSelect
                v-model:value="model.referenciaGcpj"
                placeholder="Escolha a referência GCPJ"
                :query="referenciaGcpjAndamentoSelect"
                :allow-clear="true"
              />
            </a-form-item>

            <a-form-item
              v-if="model.precisaSubsidio"
              label="Emails da agência que receberão a solicitação de subsídio"
              name="agenciaEmails"
              :rules="{
                message: 'Email da agência é obrigatório',
                required: true,
                trigger: 'change',
              }"
            >
              <a-row :gutter="8">
                <a-col flex="auto">
                  <GqlSelect
                    ref="agenciaEmailsRef"
                    v-model:value="model.agenciaEmails"
                    :query="agenciaEmailSelect"
                    :allow-clear="true"
                    :variables="{
                      where: {
                        agenciaCodigo: {
                          in: !isNilOrEmpty(data.pasta.agenciaCodigo)
                            ? [data.pasta.agenciaCodigo]
                            : [],
                        },
                      },
                    }"
                    :disabled="isNilOrEmpty(data.pasta.agenciaCodigo)"
                    placeholder="Seleciona um ou mais emails da agência"
                    mode="multiple"
                  />
                </a-col>
                <a-col flex="70px">
                  <a-button
                    style="width: 70px"
                    :disabled="isNilOrEmpty(data.pasta.agenciaCodigo)"
                    @click="() => abrirNovoAgenciaEmailDrawer(data.pasta.agenciaCodigo)"
                  >
                    Novo
                  </a-button>
                </a-col>
              </a-row>
            </a-form-item>

            <a-form-item
              v-if="model.precisaSubsidio"
              label="Documentos necessários"
              name="documentosNecessarios"
              :rules="{ required: true, message: 'Documentos necessários é obrigatório' }"
            >
              <a-input v-model:value="model.documentosNecessarios" />
            </a-form-item>

            <a-alert
              v-if="model.precisaSubsidio"
              type="warning"
              :message="data.pasta.agencia2?.regional === 2
                ? 'Uma cópia do email será enviada para: luizcl.souza@bradesco.com.br, ronaldo.g.silva@bradesco.com.br, gerenciageral@edsonrosasadvocacia.com.br, caq@edsonrosasadvocacia.com.br, cda@edsonrosasadvocacia.com.br, gdd@edsonrosasadvocacia.com.br, gerente do CDA, gerente do GDD e thaisromano@edsonrosasadvocacia.com.br'
                : 'Uma cópia do email será enviada para: ronaldo.g.silva@bradesco.com.br, gerenciageral@edsonrosasadvocacia.com.br, caq@edsonrosasadvocacia.com.br, cda@edsonrosasadvocacia.com.br, gdd@edsonrosasadvocacia.com.br, gerente do CDA, gerente do GDD e thaisromano@edsonrosasadvocacia.com.br'"
              show-icon
            />

            <div style="margin-top: 16px" />
          </template>
        </CompromissoJobCommandForm>
      </template>
    </GqlView>
  </div>
</template>
